// src/pages/CheckoutPage.tsx 
import React, { useEffect, useState } from 'react'; 
import axios from 'axios';
import { Store, Truck, MapPin, Info } from 'lucide-react';
import { useCart } from '../context/CartContext'; 
import api from '../services/api'; // (Asumimos que existe) 
import { useAuth } from '../context/AuthContext';
import { formatCurrency } from '../utils'; 

export const CheckoutPage = () => { 
  const { cartItems, loading, cartTotal } = useCart(); // Usar cartTotal del contexto 
  const { isAuthenticated, user } = useAuth();
  const [isLoadingPayment, setIsLoadingPayment] = useState(false); 
  const [shippingCost, setShippingCost] = useState(0); 
  const [formData, setFormData] = useState({ 
    customer_name: '',
    email: '', 
    phone: '', 
    shipping_address: '', 
    shipping_region: '', 
  }); 

  const [deliveryMethod, setDeliveryMethod] = useState<'pickup' | 'shipping' | null>(null);
  const [pickupLocation, setPickupLocation] = useState<'antofagasta' | 'santiago' | null>(null);

  const getStockForLocation = (locationCode: 'ANTOFAGASTA' | 'SANTIAGO') => {
    const unavailableItems: { name: string; requested: number; available: number }[] = [];
    
    cartItems.forEach(item => {
      const product = item.product;
      let available = 0;
      
      // Check variant or product stock
      const variantObj = (item as any).product_variant || item.variant;
      const locations = variantObj ? variantObj.location_inventory : product.stock_locations;
      
      if (locations) {
        const loc = locations.find((l: any) => l.location === locationCode);
        available = loc ? loc.quantity : 0;
      } else {
        // Fallback to legacy fields if location_inventory is missing
        if (locationCode === 'ANTOFAGASTA') {
           available = variantObj ? (variantObj.stock_antofagasta || 0) : (product.stock_antofagasta_total || 0);
        } else {
           available = variantObj ? (variantObj.stock_santiago || 0) : (product.stock_santiago_total || 0);
        }
      }

      if (available < item.quantity) {
        unavailableItems.push({
          name: product.name,
          requested: item.quantity,
          available: available
        });
      }
    });
    
    return unavailableItems;
  };

  const handleDeliveryMethodSelect = (method: 'pickup' | 'shipping') => {
    setDeliveryMethod(method);
    if (method === 'shipping') {
      setPickupLocation(null);
      setShippingCost(0); // Will be set when region is selected
      // Clear pickup address if present
      if (formData.shipping_address.includes('Retiro en Tienda')) {
        setFormData(prev => ({ ...prev, shipping_address: '', shipping_region: '' }));
      }
    } else {
      // Pickup selected, wait for location selection
      setShippingCost(0);
    }
  };

  const handlePickupLocationSelect = (location: 'antofagasta' | 'santiago') => {
    setPickupLocation(location);
    setShippingCost(0);
    
    const address = location === 'antofagasta' 
      ? 'Retiro en Tienda - Antofagasta' 
      : 'Retiro en Tienda - Santiago';
      
    const region = location === 'antofagasta' ? 'Antofagasta' : 'Metropolitana';

    setFormData(prev => ({
      ...prev,
      shipping_address: address,
      shipping_region: region
    }));
  };

  // Prefill form if authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      setFormData(prev => ({
        ...prev,
        customer_name: `${user.first_name || ''} ${user.last_name || ''}`.trim() || prev.customer_name,
        email: user.email || prev.email,
        phone: user.phone_number || prev.phone,
        shipping_address: user.address || prev.shipping_address,
        // shipping_region not in user serializer explicitly; leave as-is
        shipping_region: prev.shipping_region,
      }));
    }
  }, [isAuthenticated, user]);

  // Eliminar cálculo local de subtotal
  // const subtotal = cartItems.reduce((acc, item) => acc + (item.price * item.quantity), 0); 

  // Función para iniciar el pago indicando pasarela
  const handleInitiatePayment = async (gateway: 'webpay' | 'getnet') => { 
    // 1. Validar formulario (para invitados y autenticados; mantenemos requeridos simples)
    if (formData.customer_name === '' || formData.email === '' || formData.shipping_address === '' || formData.shipping_region === '' || formData.phone === '') { 
      alert("Por favor, completa toda la información de envío y contacto, incluyendo tu nombre."); 
      return; 
    } 

    setIsLoadingPayment(true); 
    try { 
      // 2. Crear la Orden (enviando el gateway elegido)
      const response = await api.post('/orders/cart/checkout/', { 
        customer_name: formData.customer_name,
        email: formData.email, 
        phone: formData.phone, 
        shipping_address: formData.shipping_address, 
        shipping_region: formData.shipping_region, 
        gateway: gateway,
      }); 

      // 3. Obtener order_id (el backend ya nos lo devuelve) 
      const { order_id } = response.data; 

      // 4. Iniciar pago con la pasarela seleccionada
      const paymentResponse = await api.post(`/payments/init/${gateway}/`, { 
        order_id: order_id 
      }); 

      console.log("Respuesta de inicio de pago:", paymentResponse);

      // 5. Redirigir a la pasarela 
      const { url } = paymentResponse.data; 
      
      if (url) {
        window.location.href = url; 
      } else {
        console.error("No se recibió URL de pago", paymentResponse.data);
        alert("Error al iniciar el pago: No se recibió la URL de redirección del servidor.");
        setIsLoadingPayment(false);
      } 

    } catch (error) { 
      console.error("Error al iniciar el pago:", error); 
      let errorMessage = "Hubo un error al procesar tu pago. Intenta de nuevo.";
      if (axios.isAxiosError(error) && error.response) {
        errorMessage = (error.response.data as any)?.detail || JSON.stringify(error.response.data);
      }
      alert(errorMessage);
      setIsLoadingPayment(false); 
    } 
  }; 

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => { 
    const { name, value } = e.target; 
    setFormData(prev => ({ ...prev, [name]: value })); 

    // --- Lógica de Envío --- 
    if (name === 'shipping_region') { 
      const cartLocation = (cartItems.find((i: any) => i.stock_location)?.stock_location) as string | undefined;
      const isAntofDest = value.toLowerCase() === 'antofagasta';
      const isAntofOrigin = cartLocation === 'ANTOFAGASTA';
      // Regla: Solo ANTOFAGASTA -> Antofagasta cuesta 3000; todo lo demás 6000
      if (isAntofDest && isAntofOrigin) {
        setShippingCost(3000);
      } else {
        setShippingCost(6000);
      }
    } 
  }; 

  const finalTotal = cartTotal + shippingCost; 

  if (loading) return <p>Cargando tu carrito...</p>; 

  return ( 
    <div className="container mx-auto p-4 md:p-12 min-h-screen pt-32" style={{ backgroundColor: '#FFFBF7' }}> 
      <h1 className="text-3xl font-semibold text-gray-800 mb-8">Checkout</h1> 
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8"> 
        {/* Columna Izquierda: Formulario y Resumen */} 
        <div className="lg:col-span-2"> 
          {/* Resumen del Pedido */} 
          <div className="bg-white p-6 shadow-md rounded-lg"> 
            <h2 className="text-xl font-semibold mb-4">Resumen de tu Pedido</h2> 
            <div className="space-y-4"> 
              {cartItems.map(item => { 
                const itemPrice = Number((item as any).unit_price) || Number((item as any).price) || 0;
                const itemQuantity = Number(item.quantity) || 0;
                const lineTotal = itemPrice * itemQuantity;
                const pv = (item as any)?.product_variant;
                const variantLabel = (item as any)?.variant_name
                  || pv?.name
                  || item.variant?.name;
                return (
                <div key={item.id} className="flex justify-between items-center border-b pb-2"> 
                  <div className="flex items-center"> 
                    <img src={item.product.image || '/images/placeholder-product.jpg'} alt={item.product.name} className="w-16 h-16 object-cover rounded mr-4" /> 
                    <div> 
                      <p className="font-semibold">{item.product.name}{variantLabel ? ` - ${variantLabel}` : ''}</p> 
                      <p className="text-sm text-gray-600">Cantidad: {itemQuantity}</p> 
                    </div> 
                  </div> 
                  <p className="font-semibold">{formatCurrency(lineTotal)}</p> 
                </div> 
              )})} 
            </div> 
          </div> 

          {/* --- INICIO FORMULARIO DE ENVÍO --- */}
          <div className="bg-white p-6 shadow-md rounded-lg mt-8"> 
            <h2 className="text-xl font-semibold mb-4">Información de Contacto</h2> 
            
            <div className="mb-4"> 
              <label htmlFor="customer_name" className="block text-sm font-medium text-gray-700">Nombre Completo</label> 
              <input type="text" name="customer_name" id="customer_name" required 
                     value={formData.customer_name} onChange={handleFormChange} 
                     className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm" 
                     placeholder="Tu nombre completo" /> 
            </div>

            <div className="mb-4"> 
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email</label> 
              <input type="email" name="email" id="email" required 
                     value={formData.email} onChange={handleFormChange} 
                     pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$"
                     title="Por favor ingresa un email válido (ej: nombre@ejemplo.com)"
                     className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm" /> 
            </div> 
            
            <div className="mb-4"> 
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700">Teléfono</label> 
              <input type="tel" name="phone" id="phone" required 
                     value={formData.phone} 
                     onChange={(e) => {
                       const value = e.target.value;
                       // Solo permitir números, espacios y el símbolo +
                       if (/^[0-9+\s]*$/.test(value)) {
                         handleFormChange(e);
                       }
                     }}
                     pattern="[0-9+\s]*"
                     title="Solo se permiten números, espacios y el símbolo +"
                     className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm" 
                     placeholder="+56 9 xxxx xxxx" /> 
            </div> 

            <h2 className="text-xl font-semibold mb-4 mt-8">Método de Entrega</h2>
            <div className="grid grid-cols-2 gap-4 mb-6">
              <button
                type="button"
                onClick={() => handleDeliveryMethodSelect('pickup')}
                className={`flex flex-col items-center justify-center p-4 border rounded-lg transition-all ${
                  deliveryMethod === 'pickup' 
                    ? 'border-[#B4897F] bg-[#FFFBF7] ring-2 ring-[#B4897F]' 
                    : 'border-gray-200 hover:border-[#B4897F]'
                }`}
              >
                <Store className={`w-8 h-8 mb-2 ${deliveryMethod === 'pickup' ? 'text-[#B4897F]' : 'text-gray-500'}`} />
                <span className={`font-medium ${deliveryMethod === 'pickup' ? 'text-[#B4897F]' : 'text-gray-700'}`}>Retiro en Tienda</span>
              </button>
              
              <button
                type="button"
                onClick={() => handleDeliveryMethodSelect('shipping')}
                className={`flex flex-col items-center justify-center p-4 border rounded-lg transition-all ${
                  deliveryMethod === 'shipping' 
                    ? 'border-[#B4897F] bg-[#FFFBF7] ring-2 ring-[#B4897F]' 
                    : 'border-gray-200 hover:border-[#B4897F]'
                }`}
              >
                <Truck className={`w-8 h-8 mb-2 ${deliveryMethod === 'shipping' ? 'text-[#B4897F]' : 'text-gray-500'}`} />
                <span className={`font-medium ${deliveryMethod === 'shipping' ? 'text-[#B4897F]' : 'text-gray-700'}`}>Envío a Domicilio</span>
              </button>
            </div>

            {deliveryMethod === 'pickup' && (
              <div className="space-y-4 animate-fadeIn">
                <h3 className="font-medium text-gray-700 mb-2">Selecciona una Sucursal:</h3>
                
                {/* Opción Antofagasta */}
                <div 
                  onClick={() => handlePickupLocationSelect('antofagasta')}
                  className={`p-4 border rounded-lg cursor-pointer transition-all ${
                    pickupLocation === 'antofagasta' ? 'border-[#B4897F] bg-[#FFFBF7]' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center">
                      <MapPin className="w-5 h-5 text-[#B4897F] mr-3" />
                      <div>
                        <p className="font-semibold text-gray-800">Sucursal Antofagasta</p>
                        <p className="text-sm text-gray-500">Retiro gratuito</p>
                      </div>
                    </div>
                    {pickupLocation === 'antofagasta' && <div className="w-4 h-4 bg-[#B4897F] rounded-full"></div>}
                  </div>
                  
                  {/* Stock Info */}
                  <div className="mt-3 pl-8 text-sm">
                    {getStockForLocation('ANTOFAGASTA').length > 0 ? (
                      <div className="text-red-600 flex items-start">
                        <Info className="w-4 h-4 mr-1 mt-0.5 flex-shrink-0" />
                        <span>
                          Algunos productos no tienen stock suficiente en esta sucursal:
                          <ul className="list-disc ml-4 mt-1">
                            {getStockForLocation('ANTOFAGASTA').map((item, idx) => (
                              <li key={idx}>{item.name} (Solicitado: {item.requested}, Disponible: {item.available})</li>
                            ))}
                          </ul>
                        </span>
                      </div>
                    ) : (
                      <p className="text-green-600 flex items-center">
                        <Info className="w-4 h-4 mr-1" /> Stock disponible para todos los productos
                      </p>
                    )}
                  </div>
                </div>

                {/* Opción Santiago */}
                <div 
                  onClick={() => handlePickupLocationSelect('santiago')}
                  className={`p-4 border rounded-lg cursor-pointer transition-all ${
                    pickupLocation === 'santiago' ? 'border-[#B4897F] bg-[#FFFBF7]' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center">
                      <MapPin className="w-5 h-5 text-[#B4897F] mr-3" />
                      <div>
                        <p className="font-semibold text-gray-800">Sucursal Santiago</p>
                        <p className="text-sm text-gray-500">Retiro gratuito</p>
                      </div>
                    </div>
                    {pickupLocation === 'santiago' && <div className="w-4 h-4 bg-[#B4897F] rounded-full"></div>}
                  </div>

                  {/* Stock Info */}
                  <div className="mt-3 pl-8 text-sm">
                    {getStockForLocation('SANTIAGO').length > 0 ? (
                      <div className="text-red-600 flex items-start">
                        <Info className="w-4 h-4 mr-1 mt-0.5 flex-shrink-0" />
                        <span>
                          Algunos productos no tienen stock suficiente en esta sucursal:
                          <ul className="list-disc ml-4 mt-1">
                            {getStockForLocation('SANTIAGO').map((item, idx) => (
                              <li key={idx}>{item.name} (Solicitado: {item.requested}, Disponible: {item.available})</li>
                            ))}
                          </ul>
                        </span>
                      </div>
                    ) : (
                      <p className="text-green-600 flex items-center">
                        <Info className="w-4 h-4 mr-1" /> Stock disponible para todos los productos
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {deliveryMethod === 'shipping' && (
              <div className="animate-fadeIn mt-4">
                <div className="mb-4"> 
                  <label htmlFor="shipping_address" className="block text-sm font-medium text-gray-700">Dirección de Envío</label> 
                  <input type="text" name="shipping_address" id="shipping_address" required 
                        value={formData.shipping_address} onChange={handleFormChange} 
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm" /> 
                </div> 
                
                <div className="mb-4"> 
                  <label htmlFor="shipping_region" className="block text-sm font-medium text-gray-700">Región</label> 
                  <select name="shipping_region" id="shipping_region" required 
                          value={formData.shipping_region} onChange={handleFormChange} 
                          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm"> 
                    <option value="" disabled>Selecciona una región...</option> 
                    <option value="Antofagasta">Antofagasta</option> 
                    <option value="Arica y Parinacota">Arica y Parinacota</option> 
                    <option value="Tarapacá">Tarapacá</option> 
                    <option value="Atacama">Atacama</option> 
                    <option value="Coquimbo">Coquimbo</option> 
                    <option value="Valparaíso">Valparaíso</option> 
                    <option value="Metropolitana">Metropolitana</option> 
                    <option value="O'Higgins">O'Higgins</option> 
                    <option value="Maule">Maule</option> 
                    <option value="Ñuble">Ñuble</option> 
                    <option value="Biobío">Biobío</option> 
                    <option value="La Araucanía">La Araucanía</option> 
                    <option value="Los Ríos">Los Ríos</option> 
                    <option value="Los Lagos">Los Lagos</option> 
                    <option value="Aysén">Aysén</option> 
                    <option value="Magallanes">Magallanes</option> 
                  </select> 
                </div>
              </div>
            )}
          </div> 
          {/* --- FIN FORMULARIO DE ENVÍO --- */} 
        </div>
 
        {/* Columna Derecha: Total y Botón de Pago */} 
        <div className="lg:col-span-1"> 
          <div className="bg-white p-6 shadow-md rounded-lg sticky top-24"> 
            <h2 className="text-xl font-semibold mb-4">Total del Pedido</h2> 
            <div className="flex justify-between mb-2"> 
              <span className="text-gray-600">Subtotal:</span> 
              <span>{formatCurrency(cartTotal)}</span> 
            </div> 
            <div className="flex justify-between mb-2"> 
              <span className="text-gray-600">Envío:</span> 
              <span>{formatCurrency(shippingCost)}</span> 
            </div> 
            <div className="border-t my-4"></div> 
            <div className="flex justify-between font-bold text-lg"> 
              <span>Total:</span> 
              <span>{formatCurrency(finalTotal)}</span> 
            </div> 
            {/* --- INICIO ZONA DE PAGO --- */}
            <div className="mt-6">
              <h2 className="text-xl font-semibold mb-4">Método de Pago</h2>
              <button
                onClick={() => handleInitiatePayment('getnet')}
                disabled={isLoadingPayment || cartItems.length === 0}
                className="w-full bg-[#B4897F] text-white py-4 font-bold text-lg hover:bg-[#A67A6F] transition-colors disabled:opacity-50 disabled:cursor-not-allowed mb-3"
              >
                {isLoadingPayment ? 'PROCESANDO PAGO...' : 'PROCEDER AL PAGO'}
              </button>
              <div className="flex justify-center">
                <img 
                  src="/images/BOTON GETNET_W120.png" 
                  alt="Getnet - Métodos de pago disponibles" 
                  className="h-12 w-auto object-contain"
                />
              </div>
            </div>
            {/* --- FIN ZONA DE PAGO --- */}
          </div> 
        </div> 
      </div> 
    </div> 
  ); 
};